import React, { useCallback } from 'react';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
  Text,
  useToast,
} from '@televet/kibble-ui';
import { useUpdateOneClinicPetMutation } from 'shared/types/graphql';

interface InactivePetModalProps {
  isCurrentlyActive?: boolean;
  petId?: string | null;
  isOpen: boolean;
  handleClose: () => void;
}

const InactivePetModal = ({ isCurrentlyActive, isOpen, petId, handleClose }: InactivePetModalProps): JSX.Element => {
  const [updateClinicPet] = useUpdateOneClinicPetMutation();
  const toast = useToast();

  const handleSubmit = useCallback(async () => {
    if (!petId) return;
    const data = {
      isActive: !isCurrentlyActive,
    };

    try {
      await updateClinicPet({
        variables: {
          where: {
            id: petId,
          },
          data,
        },
      });
      handleClose();
    } catch (e) {
      toast({
        title: 'Error!',
        status: 'error',
        description: 'There was an error when attempting to update this pet. Please try again.',
      });
    }
  }, [isCurrentlyActive, petId, toast, updateClinicPet, handleClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalHeader>Mark patient as {isCurrentlyActive ? 'inactive' : 'active'}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        {isCurrentlyActive ? (
          <Text>
            Appointments associated with inactive patients will not trigger automations or follow-up surveys. Inactive
            patients cannot be added to Appointments.
          </Text>
        ) : (
          <Text>
            Appointments associated with active patients will trigger automations and follow-up surveys. Active patients
            can be added to Appointments.
          </Text>
        )}
      </ModalBody>
      <ModalFooterButtons onSubmit={handleSubmit} submitText="Confirm" />
    </Modal>
  );
};

export default InactivePetModal;
